/**
 * The model behind the draft-contacts editor on the company form (create AND
 * edit since 2026-10-15 — on edit it adds people to an existing company).
 *
 * Split out of `CompanyContactsDraft.tsx` rather than exported beside the
 * component, following `company-master-options.ts` in this same directory: a
 * module mixing components with plain functions breaks Fast Refresh, so every
 * edit to the form remounts the editor and throws away half-typed rows.
 */

import { applyNameEdit } from '@/lib/contact-name';

/**
 * One person typed into the New company form before the company exists.
 *
 * ⚠️ **`key` is a local identity and never reaches the wire.** A draft row has
 * no server id — that is the entire reason this type exists — so React's list
 * key, the primary-contact marking and the per-row error map all hang off this
 * instead. Marking primary by ARRAY INDEX was the obvious alternative and is
 * wrong: removing row 1 silently moves the mark onto whoever is now first, and
 * nothing on screen changes to say so.
 *
 * Every value is a string because every control is a text input or a picker.
 * `''` means blank, exactly as it does in the company form's own `FormValues`,
 * and maps to an omitted key rather than to `null` when the contact is POSTed.
 */
export interface DraftContact {
  key: string;
  /** Required on the form (owner, 2026-10-16). */
  firstName: string;
  /** Optional; `''` is sent as `null`. */
  lastName: string;
  /**
   * The FULL name — the one key `POST /contacts` requires. Fills, and is filled
   * by, first + last both ways (`applyNameEdit` in `lib/contact-name.ts`).
   */
  name: string;
  email: string;
  /** `phone` on `POST /contacts` (max 32, length-only on the server). */
  phone: string;
  /** The contact-groups master's id as a string. `''` is "no group". */
  contactGroupId: string;
}

/** Client-side refusals, keyed by {@link DraftContact.key}. */
export type DraftContactErrors = Record<
  string,
  { firstName?: string; name?: string; email?: string }
>;

let draftSequence = 0;

/**
 * A blank row.
 *
 * The counter is module-scoped rather than derived from the array length, so a
 * key is never reused after a removal — React would otherwise reconcile a fresh
 * row onto a removed one's DOM node and carry its focus and error across.
 */
export function newDraftContact(): DraftContact {
  draftSequence += 1;
  return {
    key: `draft-${draftSequence}`,
    firstName: '',
    lastName: '',
    name: '',
    email: '',
    phone: '',
    contactGroupId: '',
  };
}

/** What a control in the row may change; `key` is the row's identity. */
export type DraftContactPatch = Partial<Omit<DraftContact, 'key'>>;

/**
 * Apply one edit. A name field goes through `applyNameEdit`
 * (`lib/contact-name.ts`, owner 2026-10-16: "if one is filled other 2 get auto
 * filled and vice versa"): First/Last fill Full, Full fills First/Last, but
 * only while the three agree — a deliberate mismatch is left alone. Stateless,
 * so there is no "touched" flag on the row.
 *
 * Every control sends a one-key patch; should a patch ever carry several name
 * keys, each is applied in turn.
 */
export function applyDraftContactPatch(row: DraftContact, patch: DraftContactPatch): DraftContact {
  const { firstName, lastName, name, ...rest } = patch;
  let next: DraftContact = { ...row, ...rest };
  const nameEdits = [
    ['firstName', firstName],
    ['lastName', lastName],
    ['name', name],
  ] as const;
  for (const [field, value] of nameEdits) {
    if (value === undefined) continue;
    const names = applyNameEdit(
      { firstName: next.firstName, lastName: next.lastName, name: next.name },
      field,
      value,
    );
    next = { ...next, ...names };
  }
  return next;
}

/**
 * True when nothing has been typed into the row.
 *
 * ⚠️ **A blank row is DROPPED at submit rather than refused.** Somebody who
 * presses "Add contact" and changes their mind must not be blocked from saving
 * the company by an empty box they never filled.
 */
export function isBlankDraftContact(row: DraftContact): boolean {
  return (
    row.firstName.trim() === '' &&
    row.lastName.trim() === '' &&
    row.name.trim() === '' &&
    row.email.trim() === '' &&
    row.phone.trim() === '' &&
    row.contactGroupId === ''
  );
}
